import { useState, type FormEvent } from 'react'
import type { CharacterWithStats, InventoryItem, InventoryView, Role } from '@shared/types'
import { ApiError, api } from '@/lib/api'
import { useAsync } from '@/lib/useAsync'
import {
  Badge, Button, EmptyState, ErrorNote, Field, Input, NumberField, Panel, SectionHeading, Select,
} from '@/components/ui'
import { cn } from '@/lib/cn'

export function LootTab({
  campaignId, myRole, characters,
}: {
  campaignId: string
  myRole: Role
  characters: CharacterWithStats[]
}) {
  const { data, error, loading, setData } = useAsync(() => api.getInventory(campaignId), [campaignId])
  const [actionError, setActionError] = useState<string | null>(null)
  const canWrite = myRole !== 'guest'
  const isDm = myRole === 'dm'

  async function act(action: () => Promise<InventoryView>) {
    setActionError(null)
    try {
      setData(await action())
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'That did not work')
    }
  }

  if (loading) return <Panel className="p-6 text-center text-sm text-ink-500">Loading…</Panel>
  if (error || !data) return <ErrorNote>{error ?? 'Could not load the inventory'}</ErrorNote>

  const carriers = characters.filter((character) => (data.carried[character.id]?.length ?? 0) > 0)
  const nothingAnywhere = data.pool.length === 0 && carriers.length === 0

  return (
    <div className="space-y-6">
      {actionError && <ErrorNote>{actionError}</ErrorNote>}

      {canWrite && <AddItem campaignId={campaignId} characters={characters} onDone={act} />}

      <section>
        <SectionHeading
          action={<Badge tone={data.pool.length ? 'gold' : 'neutral'}>{data.pool.length} in the pool</Badge>}
        >
          Party pool
        </SectionHeading>

        {nothingAnywhere ? (
          <EmptyState
            title="Nothing found yet"
            description="Drop loot in here as the party picks it up, then hand it out. Anything nobody is carrying stays in the pool."
          />
        ) : data.pool.length === 0 ? (
          <Panel className="p-6 text-center text-sm text-ink-500">
            The pool is empty — everything has been handed out.
          </Panel>
        ) : (
          <Panel className="divide-y divide-ink-700/60">
            {data.pool.map((item) => (
              <ItemRow
                key={item.id}
                item={item}
                characters={characters}
                canWrite={canWrite}
                isDm={isDm}
                onAssign={(characterId) => act(() => api.updateInventoryItem(campaignId, item.id, { characterId }))}
                onQty={(qty) => act(() => api.updateInventoryItem(campaignId, item.id, { qty }))}
                onToggle={(patch) => act(() => api.updateInventoryItem(campaignId, item.id, patch))}
                onDelete={() => act(() => api.deleteInventoryItem(campaignId, item.id))}
              />
            ))}
          </Panel>
        )}
      </section>

      {carriers.map((character) => (
        <section key={character.id}>
          <SectionHeading>{character.name} is carrying</SectionHeading>
          <Panel className="divide-y divide-ink-700/60">
            {(data.carried[character.id] ?? []).map((item) => (
              <ItemRow
                key={item.id}
                item={item}
                characters={characters}
                canWrite={canWrite}
                isDm={isDm}
                onAssign={(characterId) => act(() => api.updateInventoryItem(campaignId, item.id, { characterId }))}
                onQty={(qty) => act(() => api.updateInventoryItem(campaignId, item.id, { qty }))}
                onToggle={(patch) => act(() => api.updateInventoryItem(campaignId, item.id, patch))}
                onDelete={() => act(() => api.deleteInventoryItem(campaignId, item.id))}
              />
            ))}
          </Panel>
        </section>
      ))}
    </div>
  )
}

function ItemRow({
  item, characters, canWrite, isDm, onAssign, onQty, onToggle, onDelete,
}: {
  item: InventoryItem
  characters: CharacterWithStats[]
  canWrite: boolean
  isDm: boolean
  onAssign: (characterId: string | null) => void
  onQty: (qty: number) => void
  onToggle: (patch: Record<string, unknown>) => void
  onDelete: () => void
}) {
  return (
    <div className="flex flex-wrap items-center gap-3 px-4 py-3">
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm text-ink-100">
          {item.name}
          {item.qty > 1 && <span className="ml-1.5 font-mono text-xs text-ink-400">×{item.qty}</span>}
        </p>
        {item.notes && <p className="truncate text-xs text-ink-500">{item.notes}</p>}
      </div>

      {item.characterId && (
        <div className="flex shrink-0 gap-1.5">
          {(['equipped', 'attuned'] as const).map((flag) => (
            <button
              key={flag}
              type="button"
              disabled={!canWrite}
              aria-pressed={item[flag]}
              onClick={() => onToggle({ [flag]: !item[flag] })}
              className={cn(
                'rounded-full border px-2 py-0.5 text-[11px] uppercase tracking-wider transition-colors',
                item[flag]
                  ? flag === 'equipped'
                    ? 'border-moss-500/50 bg-moss-900/50 text-moss-500'
                    : 'border-arcane-500/50 bg-arcane-900/50 text-arcane-400'
                  : 'border-ink-700 text-ink-600 hover:border-ink-500',
                !canWrite && 'pointer-events-none opacity-60',
              )}
            >
              {flag}
            </button>
          ))}
        </div>
      )}

      {canWrite && (
        <>
          <NumberField
            min={1} max={9999} fallback={1} value={item.qty} onChange={onQty}
            aria-label={`Quantity of ${item.name}`} className="h-8 w-16"
          />
          <Select
            aria-label={`Who has ${item.name}`}
            value={item.characterId ?? ''}
            onChange={(e) => onAssign(e.target.value || null)}
            className="h-8 w-36 py-0 text-xs"
          >
            <option value="">Party pool</option>
            {characters.map((character) => (
              <option key={character.id} value={character.id}>{character.name}</option>
            ))}
          </Select>
        </>
      )}

      {isDm && (
        <Button variant="ghost" size="sm" className="shrink-0" onClick={onDelete}
          aria-label={`Remove ${item.name}`}>
          ✕
        </Button>
      )}
    </div>
  )
}

function AddItem({
  campaignId, characters, onDone,
}: {
  campaignId: string
  characters: CharacterWithStats[]
  onDone: (action: () => Promise<InventoryView>) => Promise<void>
}) {
  const [name, setName] = useState('')
  const [qty, setQty] = useState(1)
  const [characterId, setCharacterId] = useState('')
  const [notes, setNotes] = useState('')
  const [busy, setBusy] = useState(false)

  async function submit(event: FormEvent) {
    event.preventDefault()
    setBusy(true)
    await onDone(() => api.addInventoryItem(campaignId, {
      name: name.trim(), qty, characterId: characterId || null, notes: notes.trim(),
    }))
    setName(''); setQty(1); setNotes('')
    setBusy(false)
  }

  return (
    <Panel className="p-4">
      <SectionHeading>Add loot</SectionHeading>
      <form onSubmit={submit} className="grid gap-3 sm:grid-cols-[minmax(0,2fr)_5rem_minmax(0,1fr)_auto] sm:items-end">
        <Field label="Item">
          {(id) => <Input id={id} value={name} maxLength={120}
            onChange={(e) => setName(e.target.value)} placeholder="Potion of Healing" />}
        </Field>
        <Field label="Qty">
          {(id) => <NumberField id={id} min={1} max={9999} fallback={1} value={qty}
            onChange={setQty} className="w-full" />}
        </Field>
        <Field label="Goes to">
          {(id) => (
            <Select id={id} value={characterId} onChange={(e) => setCharacterId(e.target.value)}>
              <option value="">Party pool</option>
              {characters.map((character) => (
                <option key={character.id} value={character.id}>{character.name}</option>
              ))}
            </Select>
          )}
        </Field>
        <Button type="submit" variant="primary" busy={busy} disabled={!name.trim()}>Add</Button>

        <div className="sm:col-span-4">
          <Input value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={500}
            aria-label="Notes about this item" placeholder="Notes (optional) — where it came from, what it does…" />
        </div>
      </form>
    </Panel>
  )
}
